/**
 * View Component: ErrorBoundary
 *
 * T-Cron-2026-10-28 20:00 晚间 (T121) — 改进清单 #10「无障碍」+ 健壮性。
 *
 * 🔴 为什么这是**真缺口**而不是"锦上添花":
 *
 *   这个项目之前**一个错误边界都没有**(全树 0 处 ErrorBoundary /
 *   componentDidCatch / getDerivedStateFromError)。
 *
 *   React 的行为是:**渲染期抛出的异常会向上冒泡,直到最近的错误边界;
 *   如果一个都没有,React 会卸载整棵组件树,并在 root 上挂一个空白页。**
 *
 *   ⇒ 任何一处渲染异常(某个选题的 versions 结构异常、某个 coverUrl
 *   不是字符串、某个 map 拿到 undefined)都会让**整个看板变成白屏**,
 *   而用户看到的只是一个空白页面 —— 既不知道发生了什么,也没有
 *   任何办法恢复,只能手动刷新。
 *
 *   对一个每天用 4 次、承载 500+ 条选题的生产看板来说,「一次异常 =
 *   全站白屏 + 丢失上下文」是不可接受的故障等级。
 *
 *   边界把故障**收敛到局部**:详情抽屉里的一个按钮炸了,看板还在,
 *   用户能继续用;而且边界会:
 *     1. 展示可读的错误说明(不是 "Application error");
 *     2. 提供「重试」按钮 —— 重置 state 让子树重新渲染(对「偶发」的
 *        错误,比如某条数据临时坏掉,直接救回现场);
 *     3. 提供「刷新页面」兜底;
 *     4. 把错误详情保留在 <details> 里,便于用户截图反馈。
 *
 *   注意「不吞错」:开发态 console.error + 生产态仍然打印到 console,
 *   不假装没发生过。
 *
 * 为什么用 class 组件:
 *   `getDerivedStateFromError` / `componentDidCatch` **只存在于 class 组件**
 *   —— 函数组件 + hooks 没有等价 API(hooks 不能捕获渲染期异常)。
 *   本文件是全树**唯一**的 class 组件,其余全是函数组件。
 *
 * ⚠️ 与 React 注入的约定:
 *   vite.config.js 的 esbuild.jsxInject 会给每个 .jsx 注入
 *   `import React from 'react'`。class 组件同样受益于这个全局注入,
 *   所以本文件**不要**再显式 import React(会重复声明)。
 */

// T-Cron-2026-10-28 20:00 (T121):错误边界的样式内联在组件里。
// 为什么不新建一个 CSS 文件:
//   main.jsx 里每个 CSS 的 import 顺序都被注释严格约束
//   (app.css → a11y.css → polish.css → batch.css → drag.css → …),
//   新增一张表要多一处顺序推理、多一处未来被插队的风险。
//   而错误边界是**冷路径**(只有出错时才渲染),用内联样式最稳,
//   且不会污染主样式表的层叠顺序。
const EB = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: 'var(--bg-page, #FAF7F2)',
    color: 'var(--text-primary, #1F1D1A)',
    fontFamily: 'var(--font-sans, system-ui, sans-serif)',
  },
  card: {
    width: '100%',
    maxWidth: '480px',
    background: 'var(--bg-card, #FFFFFF)',
    border: '1px solid var(--border-card, #EDE5D5)',
    borderRadius: '14px',
    padding: '24px',
    boxShadow: 'var(--shadow-card, 0 4px 12px rgba(31,29,26,.04))',
    textAlign: 'left',
  },
  title: {
    margin: '0 0 8px',
    fontSize: '17px',
    fontWeight: '700',
    fontFamily: 'var(--font-serif, serif)',
    color: 'var(--accent, #FF2442)',
  },
  body: {
    margin: '0 0 16px',
    fontSize: '13.5px',
    lineHeight: '1.7',
    color: 'var(--text-secondary, #5E5A52)',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  btn: {
    minHeight: '40px',
    padding: '9px 16px',
    borderRadius: '999px',
    border: '1px solid var(--border-card, #EDE5D5)',
    background: 'var(--bg-card, #FFFFFF)',
    color: 'var(--text-secondary, #5E5A52)',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnPrimary: {
    background: 'var(--accent, #FF2442)',
    borderColor: 'var(--accent, #FF2442)',
    color: '#fff',
  },
  details: {
    fontSize: '12px',
    color: 'var(--text-tertiary, #8A8478)',
    borderTop: '1px solid var(--border-subtle, #E8DFD0)',
    paddingTop: '12px',
  },
  pre: {
    margin: '8px 0 0',
    padding: '10px',
    background: 'var(--bg-chip, #EFE8DC)',
    borderRadius: '8px',
    fontSize: '11px',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: '180px',
    overflow: 'auto',
    textAlign: 'left',
  },
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
    // 「重试」= 重置错误态 ⇒ 让子树重新渲染。
    // React 会在同一个元素树上重新挂载失败的子树(因为边界重新渲染时
    // children 引用变化),对「偶发」错误可以直接救回现场。
    this.handleRetry = () => this.setState({ error: null, info: null });
    this.handleReload = () => window.location.reload();
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // 不吞错:始终打到 console,便于从用户截图/反馈里定位。
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] 渲染异常:', error, info && info.componentStack);
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const stack = (info && info.componentStack) || '(无组件栈)';
    return (
      <div
        style={EB.wrap}
        role="alert"
        aria-live="assertive"
        aria-label="看板渲染出错"
      >
        <div style={EB.card}>
          <h1 style={EB.title}>看板出了点问题</h1>
          <p style={EB.body}>
            这一页在渲染时遇到错误,已经停在这里,没有影响你的选题数据。
            可以先点「重试」再试一次;如果还是不行,刷新页面通常能恢复。
          </p>
          <div style={EB.actions}>
            <button
              type="button"
              style={{ ...EB.btn, ...EB.btnPrimary }}
              onClick={this.handleRetry}
              aria-label="重试渲染,重新加载当前区域内容"
            >
              重试
            </button>
            <button
              type="button"
              style={EB.btn}
              onClick={this.handleReload}
              aria-label="刷新整个页面"
            >
              刷新页面
            </button>
          </div>
          <details style={EB.details} aria-label="错误技术详情">
            <summary aria-label="展开技术详情,方便截图反馈">技术详情(方便反馈)</summary>
            <pre style={EB.pre}>
              {String(error && (error.stack || error.message || error))}
              {'\n\n'}
              {stack}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

window.ErrorBoundary = ErrorBoundary;

export default ErrorBoundary;
